import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { serverInfo } from "../shared/config/serverConfig";
import { apiFetch } from "../shared/api/apiFetch";
import {
  clearSmsOptInRemindLater,
  needsSmsOptInPrompt,
  setSmsOptInRemindLater,
  SMS_OPT_IN_SOURCES,
  wasSmsOptInRemindLater,
} from "../shared/utils/smsOptIn";
import ConfirmationModal from "./ConfirmationModal";
import SmsOptInConsentBlock from "./SmsOptInConsentBlock";

/**
 * One-time prompt for existing users who have a phone but no SMS consent on file.
 * Mount once under Nav (covers Event Calendar and authenticated app pages).
 */
export default function SmsOptInPromptModal() {
  const { userInfo, setUserInfo } = useAuth();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!userInfo?._id) {
      setOpen(false);
      setChecked(false);
      return;
    }
    if (!needsSmsOptInPrompt(userInfo)) {
      setOpen(false);
      return;
    }
    if (wasSmsOptInRemindLater(userInfo._id)) {
      setOpen(false);
      return;
    }
    setChecked(false);
    setOpen(true);
  }, [userInfo]);

  if (!open || !userInfo?._id) return null;

  const savePreference = async () => {
    const account_id = userInfo.account_id != null ? String(userInfo.account_id).trim() : "";
    const _id = String(userInfo._id).trim();
    if (!account_id || !_id) {
      throw new Error("Unable to save SMS preference: missing account.");
    }
    const resp = await apiFetch(`${serverInfo.serverUrl}/updateUser`, {
      method: "POST",
      body: JSON.stringify({
        _id,
        account_id,
        sms_opt_in: !!checked,
        sms_opt_in_source: SMS_OPT_IN_SOURCES.PROFILE_PROMPT,
      }),
    });
    const text = await resp.text().catch(() => "");
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }
    if (!resp.ok) {
      throw new Error((data && data.message) || text || "Could not save SMS preference.");
    }
    clearSmsOptInRemindLater();
    const next = {
      ...userInfo,
      sms_opt_in: !!checked,
      sms_opt_in_at: new Date().toISOString(),
      sms_opt_in_source: SMS_OPT_IN_SOURCES.PROFILE_PROMPT,
    };
    setUserInfo(next);
    setOpen(false);
  };

  const remindLater = () => {
    setSmsOptInRemindLater(userInfo._id);
    setOpen(false);
  };

  return (
    <ConfirmationModal
      open={open}
      title="Text message updates"
      mode="confirm"
      confirmLabel="Save preference"
      cancelLabel="Remind me later"
      contentMaxWidth={520}
      closeOnBackdrop={false}
      closeOnEscape={false}
      onCancel={remindLater}
      onConfirm={savePreference}
      message={
        <div>
          <p style={{ margin: "0 0 10px", fontSize: 13, color: "#374151", lineHeight: 1.45 }}>
            We can text you about programs and events at the phone number on your profile. Checking
            the box is optional — you can still use the app either way.
          </p>
          <SmsOptInConsentBlock
            checkboxId="sms-opt-in-prompt"
            checked={checked}
            onChange={setChecked}
          />
        </div>
      }
    />
  );
}
